import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Button, Card } from "flowbite-react";
import Input from "../inputs/Input";
import SelectInput from "../inputs/SelectInput";
import CPFInput from "../inputs/CPFInput";
import AutoCompleteInput from "../inputs/Autocomplete";
import ComunicateSellSection from "./ComunicateSellSection";
import * as yup from "yup";
import { SURVEYS, USERS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useQuery } from "react-query";
import { useEffect, useState } from "react";
import CNPJInput from "../inputs/CNPJInput";
import useCEP from "../../hooks/useCEP";
import PhoneInput from "../inputs/PhoneInput";
import { isValidCNPJ, isValidCPF } from "../../utils/utils";

const schema = yup.object().shape({
  nomeComprador: yup.string().required("O nome é obrigatório"),
  cpfCnpjComprador: yup
    .string()
    .required("O documento é obrigatório")
    .test("cpfCnpj", "Documento inválido", function (value) {
      if (!value) return false;
      const documento = value.replace(/\D/g, "");

      if (documento.length === 11) {
        return isValidCPF(documento);
      } else if (documento.length === 14) {
        return isValidCNPJ(documento);
      }
      return false;
    }),
  cepComprador: yup.string().required("O CEP é obrigatório"),
  logradouroComprador: yup.string().required("O endereço é obrigatório"),
  bairroComprador: yup.string().required("O bairro é obrigatório"),
  numeroEnderecoComprador: yup.string().required("O número é obrigatório"),
  codigoMunicipioComprador: yup.string().required("O município é obrigatório"),
});
const BuyerData = ({
  setActiveStep,
  communicateSell,
  setCommunicateSell,
  states,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [municipios, setMunicipios] = useState([]);
  const [isLoadingMunicipios, setIsLoadingMunicipios] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const {
    cep,
    setCep,
    address,
    loading: loadingCEP,
    error: errorCEP,
  } = useCEP();

  const onBlurSearchCEP = (e) => {
    setCep(getValues("cepComprador"));
  };

  useEffect(() => {
    setValue("logradouroComprador", address?.logradouro?.toUpperCase());
    setValue("bairroComprador", address?.bairro?.toUpperCase());
    setValue("codigoMunicipioComprador", address?.localidade?.toUpperCase());
  }, [address]);

  useEffect(() => {
    if (communicateSell) {
      Object.entries(communicateSell).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          setValue(key, value);
        }
      });
    }
  }, [communicateSell, setValue]);

  useEffect(() => {
    setIsLoadingMunicipios(true);
    const getMunicipios = async () => {
      const response = await axios.get(
        `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${watch(
          "ufComprador"
        )}/municipios?orderBy=nome`
      );

      setMunicipios(
        response?.data?.map((municipio) => ({
          value: municipio.id,
          label: municipio.nome,
        }))
      );
      setIsLoadingMunicipios(false);
    };

    getMunicipios();
  }, [watch("ufComprador")]);

  const onBack = async () => {
    setActiveStep(3);
  };

  const onSubmit = async (data) => {
    setCommunicateSell((prev) => ({ ...prev, ...data }));

    setActiveStep(5);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ComunicateSellSection name="Dados do comprador">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <SelectInput
              register={register}
              name="codigoTipoPessoaComprador"
              label="Tipo de pessoa"
            >
              <option value={0}>Pessoa Física</option>
              <option value={1}>Pessoa Jurídica</option>
            </SelectInput>
            {errors.codigoTipoPessoaComprador?.message && (
              <small className="text-red-600">
                {errors.codigoTipoPessoaComprador?.message}
              </small>
            )}
          </div>
          <div>
            <Input
              register={register}
              name="nomeComprador"
              label="Nome do comprador *"
              required={true}
              uppercase
            />
            {errors.nomeComprador?.message && (
              <small className="text-red-600">
                {errors.nomeComprador?.message}
              </small>
            )}
          </div>

          <div>
            {watch("codigoTipoPessoaComprador") == 0 ? (
              <CPFInput
                register={register}
                name="cpfCnpjComprador"
                label="CPF do comprador *"
                required={true}
              />
            ) : (
              <CNPJInput
                register={register}
                name="cpfCnpjComprador"
                label="CNPJ do comprador *"
                required={true}
              />
            )}
            {errors.cpfCnpjComprador?.message && (
              <small className="text-red-600">
                {errors.cpfCnpjComprador?.message}
              </small>
            )}
          </div>
          <div>
            <PhoneInput
              register={register}
              name="telefoneComprador"
              label="Telefone do comprador"
              mask="CUSTOM"
            />
            {errors.telefoneComprador?.message && (
              <small className="text-red-600">
                {errors.telefoneComprador?.message}
              </small>
            )}
          </div>
        </div>
      </ComunicateSellSection>

      <ComunicateSellSection name="Endereço do comprador">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              register={register}
              name="cepComprador"
              label="CEP *"
              required={true}
              onBlur={onBlurSearchCEP}
            />
            {errors.cepComprador?.message && (
              <small className="text-red-600">
                {errors.cepComprador?.message}
              </small>
            )}
          </div>

          <div>
            <Input
              register={register}
              name="logradouroComprador"
              label="Endereço *"
              required={true}
              uppercase
            />
            {errors.logradouroComprador?.message && (
              <small className="text-red-600">
                {errors.logradouroComprador?.message}
              </small>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <div>
            <Input
              register={register}
              name="bairroComprador"
              label="Bairro *"
              required={true}
            />
            {errors.bairroComprador?.message && (
              <small className="text-red-600">
                {errors.bairroComprador?.message}
              </small>
            )}
          </div>

          <div>
            <Input
              register={register}
              name="numeroEnderecoComprador"
              label="Número *"
              required={true}
            />
            {errors.numeroEnderecoComprador?.message && (
              <small className="text-red-600">
                {errors.numeroEnderecoComprador?.message}
              </small>
            )}
          </div>

          <div>
            <Input
              register={register}
              name="complementoComprador"
              label="Complemento"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <AutoCompleteInput
              register={register}
              name="ufComprador"
              label="UF do comprador *"
              options={states}
              value={watch("ufComprador")}
              onChange={(user) => setValue("ufComprador", user.value)}
              required={true}
            />
            {errors.ufComprador?.message && (
              <small className="text-red-600">
                {errors.ufComprador?.message}
              </small>
            )}
          </div>

          <div>
            <AutoCompleteInput
              register={register}
              name="codigoMunicipioComprador"
              label="Município do comprador *"
              options={municipios}
              value={watch("codigoMunicipioComprador")}
              onChange={(user) =>
                setValue("codigoMunicipioComprador", user.value)
              }
              required={true}
              isLoading={isLoadingMunicipios}
            />
            {errors.codigoMunicipioComprador?.message && (
              <small className="text-red-600">
                {errors.codigoMunicipioComprador?.message}
              </small>
            )}
          </div>
        </div>
      </ComunicateSellSection>

      <div className="flex justify-center items-center gap-2 mt-10">
        <Button
          className="bg-gray-500"
          onClick={onBack}
          style={{ minWidth: "90px" }}
        >
          Voltar
        </Button>
        <Button
          type="submit"
          className="primary"
          disabled={isSubmitting}
          isProcessing={isSubmitting}
        >
          {!isSubmitting ? "Próximo" : "Salvando..."}
        </Button>
      </div>
      {error && (
        <Alert color="failure" withBorderAccent className="mt-4">
          Erro ao tentar salvar o laudo! Tente novamente mais tarde.
        </Alert>
      )}
    </form>
  );
};

export default BuyerData;
